import { cn } from "../../lib/utils";
import { Separator } from "../ui/separator";
import { Stepper, StepperIndicator, StepperItem, StepperSeparator, StepperTitle, StepperTrigger } from "../ui/stepper";
import { CheckIcon } from "lucide-react";

type Step = {
	step: number;
	title: string;
};

type StepperComponentProps = {
	steps: Step[];
	disableFreeNavigation?: boolean;
	currentStepIndex: number;
	setCurrentStepIndex?: (step: number) => void;
	asChild?: boolean;
	parentPaddingOffsetPx?: number;
	className?: string;
	showSeparator?: boolean;
	canNavigateFromTo?: (from: number, to: number) => boolean;
};

export const HorizontalStepper = ({
	steps,
	currentStepIndex,
	setCurrentStepIndex,
	asChild = false,
	disableFreeNavigation = false,
	parentPaddingOffsetPx,
	className,
	showSeparator = true,
	canNavigateFromTo,
}: StepperComponentProps) => {
	const rootPadding = parentPaddingOffsetPx ? `ml-[-${parentPaddingOffsetPx}px] mr-[-${parentPaddingOffsetPx}px]` : "";
	const contentPadding = parentPaddingOffsetPx ? `pl-${parentPaddingOffsetPx / 4} pr-${parentPaddingOffsetPx / 4}` : "";

	return (
		<div className={cn("bg-[#FDFDFD]", rootPadding)}>
			{showSeparator && <Separator />}
			<div className={cn("space-y-8 text-center my-4", contentPadding, className)}>
				<Stepper value={currentStepIndex} onValueChange={setCurrentStepIndex} orientation="horizontal">
					{steps.map(({ step, title }) => {
						const isCompleted = step < currentStepIndex;
						const isActive = step === currentStepIndex;

						return (
							<StepperItem key={step} step={step} className="not-last:flex-1">
								<StepperTrigger className="flex items-center gap-3" asChild={asChild} onClick={() => {
									if (disableFreeNavigation) {
										return;
									}

									if (canNavigateFromTo && !canNavigateFromTo(currentStepIndex, step)) {
										return;
									}

									setCurrentStepIndex?.(step);
								}}>
									<>
										<StepperIndicator
											className={`flex size-6 items-center justify-center rounded-full border text-white ${
												isCompleted
													? "border-blue-100"
													: isActive
													? "border-blue-100"
													: "bg-white text-gray-500 border-gray-300"
											}`}
											asChild
										>
											{isCompleted ? <CheckIcon size={16} /> : <span className="text-sm font-medium">{step}</span>}
										</StepperIndicator>
										<StepperTitle
											className={`text-sm font-medium text-left px-2 py-1 rounded ${
												isCompleted || isActive ? "text-primary" : ""
											}`}
										>
											{title}
										</StepperTitle>
									</>
								</StepperTrigger>

								{step < steps.length && <StepperSeparator className="max-md:mt-3.5 md:mx-4" />}
							</StepperItem>
						);
					})}
				</Stepper>
			</div>
			{showSeparator && <Separator />}
		</div>
	);
};

export default HorizontalStepper;
